import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  FavoriteHymn,
  copyFavoritesToClipboard,
  exportFavoritesToFile,
  generateFavoritesExportText,
  sendFavoritesByEmail,
} from "@/utils/export-favorites";
import {
  Check,
  ChevronDown,
  Copy,
  Download,
  FileText,
  FolderInput,
  Mail,
  Share2,
} from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";

interface ExportFavoritesModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  favorites: FavoriteHymn[];
  onOpenImport?: () => void;
}

export const ExportFavoritesModal: React.FC<ExportFavoritesModalProps> = ({
  isOpen,
  onOpenChange,
  favorites,
  onOpenImport,
}) => {
  const [copied, setCopied] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const previewText = generateFavoritesExportText(favorites);

  const handleDownloadFile = () => {
    const success = exportFavoritesToFile(favorites);
    if (success) {
      toast.success("Fichier de favoris téléchargé (.txt)");
      onOpenChange(false);
    } else {
      toast.error("Impossible d'exporter le fichier");
    }
  };

  const handleSendEmail = () => {
    const success = sendFavoritesByEmail(favorites);
    if (success) {
      toast.success("Client de messagerie ouvert");
      onOpenChange(false);
    } else {
      toast.error("Impossible d'ouvrir le client de messagerie");
    }
  };

  const handleCopyClipboard = async () => {
    const success = await copyFavoritesToClipboard(favorites);
    if (success) {
      setCopied(true);
      toast.success("Liste copiée !");
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Échec de la copie");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-sm sm:max-w-md bg-slate-900 border border-slate-800 text-white p-3.5 sm:p-5 rounded-2xl sm:rounded-3xl shadow-2xl backdrop-blur-xl max-h-[88vh] overflow-y-auto scrollbar-tiny">
        {/* Header Compact */}
        <DialogHeader className="text-left space-y-1 sm:space-y-1.5 pr-6">
          <div className="flex items-center gap-2 sm:gap-2.5">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-400 flex-shrink-0">
              <Share2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-sm sm:text-base font-bold text-white tracking-tight truncate">
                Exporter mes favoris
              </DialogTitle>
              <DialogDescription className="text-[10px] sm:text-xs text-slate-400 leading-tight">
                {favorites.length} cantique{favorites.length > 1 ? "s" : ""}{" "}
                sélectionné{favorites.length > 1 ? "s" : ""}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* 2 Main Actions (File vs Email) */}
        <div className="grid grid-cols-2 gap-2 mt-2.5 sm:mt-3">
          {/* Option 1: Télécharger le fichier */}
          <button
            type="button"
            onClick={handleDownloadFile}
            className="group flex flex-col items-center sm:items-start p-2.5 sm:p-3 rounded-xl bg-slate-950/70 hover:bg-slate-800/90 border border-slate-800 hover:border-emerald-500/50 transition-all text-center sm:text-left cursor-pointer active:scale-95"
          >
            <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-400 mb-1.5 group-hover:scale-105 transition-transform">
              <Download className="w-3.5 h-3.5" />
            </div>
            <span className="text-xs font-bold text-slate-100 group-hover:text-emerald-300 transition-colors">
              Fichier .txt
            </span>
            <span className="text-[9px] sm:text-[10px] text-slate-400 line-clamp-1">
              Télécharger
            </span>
          </button>

          {/* Option 2: Envoyer par e-mail */}
          <button
            type="button"
            onClick={handleSendEmail}
            className="group flex flex-col items-center sm:items-start p-2.5 sm:p-3 rounded-xl bg-slate-950/70 hover:bg-slate-800/90 border border-slate-800 hover:border-sky-500/50 transition-all text-center sm:text-left cursor-pointer active:scale-95"
          >
            <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-sky-500/15 border border-sky-500/30 flex items-center justify-center text-sky-400 mb-1.5 group-hover:scale-105 transition-transform">
              <Mail className="w-3.5 h-3.5" />
            </div>
            <span className="text-xs font-bold text-slate-100 group-hover:text-sky-300 transition-colors">
              Par E-mail
            </span>
            <span className="text-[9px] sm:text-[10px] text-slate-400 line-clamp-1">
              Envoyer la liste
            </span>
          </button>
        </div>

        {/* Option 3: Copier dans le presse-papiers */}
        <button
          type="button"
          onClick={handleCopyClipboard}
          className="w-full flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700 text-slate-200 hover:text-white text-[11px] sm:text-xs font-semibold transition-all cursor-pointer active:scale-98"
        >
          {copied ? (
            <>
              <Check className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-emerald-300 font-bold">
                Copié dans le presse-papiers !
              </span>
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5 text-slate-400" />
              <span>Copier tout le texte</span>
            </>
          )}
        </button>

        {/* Section Aperçu Repliable pour économiser l'espace */}
        <div className="mt-1 border-t border-slate-800/80 pt-2">
          <button
            type="button"
            onClick={() => setShowPreview((prev) => !prev)}
            className="w-full flex items-center justify-between text-[10px] sm:text-[11px] text-slate-400 hover:text-slate-200 font-medium py-1 transition-colors cursor-pointer"
          >
            <span className="flex items-center gap-1.5">
              <FileText className="w-3 h-3 text-slate-500" />
              <span>Aperçu de la liste (Section - N° - Titre : lien)</span>
            </span>
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform duration-200 ${
                showPreview ? "rotate-180 text-emerald-400" : ""
              }`}
            />
          </button>

          {showPreview && (
            <div className="mt-1.5 max-h-28 sm:max-h-36 overflow-y-auto p-2 sm:p-2.5 rounded-lg bg-slate-950 border border-slate-800/90 text-[10px] text-slate-300 font-mono leading-relaxed whitespace-pre-wrap select-all scrollbar-tiny animate-in fade-in duration-150">
              {previewText || "Aucun favori"}
            </div>
          )}
        </div>

        {onOpenImport && (
          <div className="pt-2 border-t border-slate-800/80 text-center">
            <button
              type="button"
              onClick={onOpenImport}
              className="inline-flex items-center gap-1.5 text-[11px] text-sky-400 hover:text-sky-300 font-medium transition-colors cursor-pointer"
            >
              <FolderInput className="w-3.5 h-3.5" />
              <span>Vous avez une liste exportée ? Importer des favoris</span>
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
